import { CustomLink } from '@/components/atoms/CustomLink/CustomLink';
import { TTextMultilanguage } from '@/types/global';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ITabsAndFilterGroups } from '../../FilteredProjectsGallery/FilteredProjectsGallery.types';
import { usePathname } from 'next/navigation';
import { Typography } from '@/components/atoms/Typography/Typography';
import { Button } from '@/components/atoms/Button/Button';
import { usePageStore } from '@/store/usePageStore';
import { defaultLocale, localizedSlugs } from '@/i18n/routing';
import * as Styled from './NavMobile.styled';
import { replaceServicesOldWithNew } from '@/utils/generic';


interface INavMobileProps {
  filtersData: ITabsAndFilterGroups | undefined;
  isPrivatePresentation: boolean;
}

export const NavMobile = ({ filtersData }: INavMobileProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [heightMobileLinks, setHeightMobileLinks] = useState('auto');
  const refLinksContainer = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const locale = usePageStore((s) => s.locale);

  useLayoutEffect(() => {
    const height = refLinksContainer.current?.scrollHeight || 0;
    setHeightMobileLinks(height ? `${height}px` : 'auto');
  }, []);

  const isMainWorkTab = useMemo(() => {
    return pathname === `/${locale !== defaultLocale ? `${locale}/` : ''}${localizedSlugs.work[locale || 'en']}`;
  }, [pathname, locale]);

  const currentWorkTab: string | null = useMemo(() => {
    if (!isMainWorkTab) {
      const text = pathname.split('/').slice(-1)[0].replace(/-/g, ' ');
      return text.charAt(0).toUpperCase() + text.slice(1);
    }

    return null;
  }, [pathname, isMainWorkTab]);

  const isCurrentWorkTab = (text: string): boolean => {
    const currentSlug = pathname.split('/').slice(-1)[0];
    const tabSlug = text.toLowerCase().replace(/ /g, '-');

    const textReplaced = replaceServicesOldWithNew(tabSlug, locale).replace('ó', 'o');
    return currentSlug.replace('-', ' ') === textReplaced.replace('-', ' ');
  }

  return (
    <Styled.SelectorContainer
      data-is-open={isMenuOpen}
      className="display-only-mobile"
    >
      <Button
        callbackOnClick={() => {
          setIsMenuOpen(!isMenuOpen);
        }}
      >
        <Typography
          text={
            currentWorkTab || {
              en: 'All Projects',
              es: 'Todos los proyectos',
            }
          }
          size="lg"
          className="title-all-projects"
        />
        <svg
          width="12"
          height="7"
          viewBox="0 0 12 7"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M1 1L6 6L11 1" stroke="#696966" strokeLinecap="round" />
        </svg>
      </Button>
      <Styled.LinksContainer
        ref={refLinksContainer}
        data-is-displayed={isMenuOpen}
        style={
          {
            '--height-expanded': `${heightMobileLinks}`,
          } as React.CSSProperties
        }
      >
        {!isMainWorkTab && (
          <CustomLink
            text={{
              en: 'All Projects',
              es: 'Todos los proyectos',
            }}
            slug={`/${localizedSlugs.work[locale || 'en']}`}
            lowercase={true}
            color={'secondary'}
          />
        )}
        {filtersData?.tabs.map(
          (
            tab: {
              text: string | TTextMultilanguage | undefined;
              slug: string;
            },
            i: number
          ) => {
            const tabText = tab.text as string;
            return (
              (!currentWorkTab || (tabText && !isCurrentWorkTab(tabText))) && (
                <CustomLink
                  text={replaceServicesOldWithNew(tabText, locale)}
                  key={`btn-filter-${i}`}
                  slug={tab.slug}
                  lowercase={true}
                  color={'secondary'}
                  {...{ scroll: true }}
                />
              )
            );
          }
        )}
      </Styled.LinksContainer>
    </Styled.SelectorContainer>
  );
};
